import {
  NPM_REGISTRY_BASE_URL,
  ORGX_WIZARD_NPM_PACKAGE_NAME,
} from "../constants.js";
import { isRecord } from "./fs.js";

export interface NpmRegistryCheck {
  ok: boolean;
  skipped: boolean;
  reachable: boolean;
  published: boolean;
  url: string;
  packageName: string;
  version?: string;
  error?: string;
  details: string[];
}

function resolvePackageUrl(packageName: string): string {
  return `${NPM_REGISTRY_BASE_URL}/${encodeURIComponent(packageName)}`;
}

function getLatestVersion(payload: unknown): string | undefined {
  if (!isRecord(payload) || !isRecord(payload["dist-tags"])) {
    return undefined;
  }

  const latest = payload["dist-tags"].latest;
  return typeof latest === "string" && latest.trim().length > 0 ? latest : undefined;
}

export async function checkNpmRegistryHealth(
  packageName: string = ORGX_WIZARD_NPM_PACKAGE_NAME,
): Promise<NpmRegistryCheck> {
  const url = resolvePackageUrl(packageName);

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: AbortSignal.timeout(7_000),
    });

    const text = await response.text();
    let payload: unknown = text;
    try {
      payload = text.length > 0 ? JSON.parse(text) : null;
    } catch {
      payload = text;
    }

    if (response.ok) {
      const version = getLatestVersion(payload);
      return {
        ok: true,
        skipped: false,
        reachable: true,
        published: true,
        url,
        packageName,
        ...(version ? { version } : {}),
        details: [
          `http status: ${response.status}`,
          ...(version ? [`latest version: ${version}`] : ["latest version: unavailable"]),
        ],
      };
    }

    if (response.status === 404) {
      return {
        ok: true,
        skipped: false,
        reachable: true,
        published: false,
        url,
        packageName,
        error: `${packageName} is not published on npm yet.`,
        details: [`http status: ${response.status}`],
      };
    }

    return {
      ok: false,
      skipped: false,
      reachable: false,
      published: false,
      url,
      packageName,
      error: `npm registry returned HTTP ${response.status}`,
      details: [`http status: ${response.status}`],
    };
  } catch (error) {
    return {
      ok: false,
      skipped: false,
      reachable: false,
      published: false,
      url,
      packageName,
      error: error instanceof Error ? error.message : String(error),
      details: [],
    };
  }
}
